import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { fetchActivePriorityCount } from '@/api/buckets'
import { useBuckets, useArchiveBucket } from '@/hooks/useBuckets'
import type { Bucket } from '@/types'

interface DeleteBucketDialogProps {
  bucket: Bucket | null
  onOpenChange: (open: boolean) => void
}

export function DeleteBucketDialog({ bucket, onOpenChange }: DeleteBucketDialogProps) {
  const allBuckets = useBuckets()
  const [priorityCount, setPriorityCount] = useState(0)
  const [targetId, setTargetId] = useState<string>('')
  const [deleting, setDeleting] = useState(false)
  const archiveBucket = useArchiveBucket()

  const open = bucket !== null

  // When a bucket is selected for deletion, count its priorities and set default target
  useEffect(() => {
    if (!bucket || !allBuckets) return
    void fetchActivePriorityCount(bucket.id).then(setPriorityCount)
    const systemBucket = allBuckets.find((b) => b.isSystem)
    setTargetId(systemBucket?.id ?? '')
  }, [bucket, allBuckets])

  const otherBuckets = allBuckets?.filter((b) => b.id !== bucket?.id) ?? []
  const systemBucket = otherBuckets.find((b) => b.isSystem)
  const userBuckets = otherBuckets.filter((b) => !b.isSystem)

  async function handleConfirm() {
    if (!bucket || !targetId) return
    setDeleting(true)
    try {
      await archiveBucket.mutateAsync({ id: bucket.id, priorityDisposition: targetId })
      onOpenChange(false)
    } finally {
      setDeleting(false)
    }
  }

  if (!bucket) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove "{bucket.name}"?</DialogTitle>
          <DialogDescription>
            This area will be archived and removed from view. You can restore it within 30 days.
          </DialogDescription>
        </DialogHeader>

        {priorityCount > 0 && (
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              {priorityCount} {priorityCount === 1 ? 'priority' : 'priorities'} will be moved to
              another area.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="disposition">Move priorities to</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger id="disposition">
                  <SelectValue placeholder="Choose an area…" />
                </SelectTrigger>
                <SelectContent>
                  {userBuckets.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: b.color }}
                        />
                        {b.name}
                      </span>
                    </SelectItem>
                  ))}
                  {systemBucket && (
                    <SelectItem value={systemBucket.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: systemBucket.color }}
                        />
                        {systemBucket.name} (unsorted)
                      </span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={deleting || (priorityCount > 0 && !targetId)}
            onClick={handleConfirm}
          >
            {deleting ? 'Removing…' : 'Remove area'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
